import { useCallback, useEffect, useState } from 'react';
import api from '../utils/api';

const endpointByType = {
  start_production: 'start-production',
  create_storyboard: 'storyboard',
  generate_assets: 'generate-assets',
  generate_keyframes: 'generate-keyframes',
  submit_videos: 'submit-videos',
};

function clientId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export default function useCanvasAgent(canvasId, onActionResult) {
  const [messages, setMessages] = useState([]);
  const [actions, setActions] = useState([]);
  const [stage, setStage] = useState({ stage: 'idle', blocker: '', next: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!canvasId) return;
    try {
      const response = await api.get(`/canvas/${canvasId}/agent/messages`);
      setMessages(response.data.messages || []);
      setActions(response.data.actions || []);
      setStage(response.data.stage || { stage: 'idle', blocker: '', next: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Agent 对话恢复失败');
    }
  }, [canvasId]);

  useEffect(() => { load(); }, [load]);

  const sendMessage = useCallback(async (message, context = {}) => {
    const text = String(message || '').trim();
    if (!canvasId || !text || loading) return null;
    const id = clientId();
    const optimistic = {
      id: `optimistic-${id}`,
      role: 'user',
      content: text,
      message_type: 'text',
      metadata: {},
      created_at: new Date().toISOString(),
    };
    setMessages(current => [...current, optimistic]);
    setLoading(true);
    setError('');
    try {
      const response = await api.post(`/canvas/${canvasId}/agent/chat`, {
        message: text,
        clientMessageId: id,
        ...context,
      });
      setMessages(current => [
        ...current.filter(item => item.id !== optimistic.id),
        { ...optimistic, id: `user-${id}` },
        response.data.message,
      ]);
      if (response.data.action) {
        setActions(current => [...current.filter(item => item.id !== response.data.action.id), response.data.action]);
      }
      setStage(response.data.stage || stage);
      return response.data;
    } catch (err) {
      setMessages(current => current.filter(item => item.id !== optimistic.id));
      setError(err.response?.data?.error || '消息发送失败');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [canvasId, loading, stage]);

  const createAction = useCallback(async (actionType, payload = {}) => {
    if (!canvasId) return null;
    setLoading(true);
    setError('');
    try {
      const response = await api.post(`/canvas/${canvasId}/agent/actions`, {
        actionType,
        payload,
        idempotencyKey: `ui:${actionType}:${clientId()}`,
      });
      setActions(current => [...current.filter(item => item.id !== response.data.id), response.data]);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || '确认卡创建失败');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [canvasId]);

  const confirmAction = useCallback(async (action, extra = {}) => {
    if (!canvasId || !action?.id || loading) return null;
    setLoading(true);
    setError('');
    try {
      const confirmed = await api.post(`/canvas/${canvasId}/agent/actions/${action.id}/confirm`);
      setActions(current => current.map(item => item.id === action.id ? confirmed.data : item));
      const endpoint = endpointByType[action.action_type];
      if (!endpoint) return confirmed.data;
      const executed = await api.post(`/canvas/${canvasId}/agent/${endpoint}`, { actionId: action.id, ...(extra || {}) });
      setActions(current => current.filter(item => item.id !== action.id));
      await onActionResult?.(action.action_type, executed.data);
      await load();
      return executed.data;
    } catch (err) {
      setError(err.response?.data?.error || '动作执行失败');
      await load();
      throw err;
    } finally {
      setLoading(false);
    }
  }, [canvasId, loading, load, onActionResult]);

  const cancelAction = useCallback(async (action) => {
    if (!canvasId || !action?.id || loading) return;
    setLoading(true);
    setError('');
    try {
      await api.post(`/canvas/${canvasId}/agent/actions/${action.id}/cancel`);
      setActions(current => current.filter(item => item.id !== action.id));
    } catch (err) {
      setError(err.response?.data?.error || '取消失败');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [canvasId, loading]);

  return {
    messages,
    actions,
    stage,
    loading,
    error,
    setError,
    load,
    sendMessage,
    createAction,
    confirmAction,
    cancelAction,
  };
}
